import React, { useState, useMemo, useRef } from "react";
import Hero from "../../components/Hero/Hero";
import PageFooter from "../../components/PageFooter/PageFooter";
import "./reviewCalculator.css";
import Seo from "../../i18n/Seo";
import useLang from "../../i18n/useLang";
import content from "./content";

const PRESETS = [4.0, 4.2, 4.5, 4.7, 4.9];

const ReviewCalculator = () => {
  const lang = useLang();
  const t = content[lang];
  const [reviewCount, setReviewCount] = useState("");
  const [currentRating, setCurrentRating] = useState("");
  const [targetRating, setTargetRating] = useState("");
  const calcRef = useRef(null);

  const scrollToCalc = () => {
    calcRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const result = useMemo(() => {
    const count = parseFloat(reviewCount);
    const current = parseFloat(currentRating);
    const target = parseFloat(targetRating);

    if (!count || !current || !target) return null;
    if (count <= 0 || current <= 0 || current > 5) return null;
    if (target <= current) return { needed: 0, alreadyMet: true };
    if (target >= 5) return { needed: Infinity, unreachable: true };

    const needed = Math.ceil(
      (count * (target - current)) / (5 - target)
    );

    const newTotal = count + needed;
    const newRating = ((count * current) + (needed * 5)) / newTotal;

    return { needed, newTotal, newRating: newRating.toFixed(2) };
  }, [reviewCount, currentRating, targetRating]);

  return (
    <div className="calc-page">
      <Seo
        routeId="reviewCalculator"
        title={t.seo.title}
        description={t.seo.description}
        ogTitle={t.seo.ogTitle}
        ogDescription={t.seo.ogDescription}
      />

      <Hero
        title={t.hero.title}
        subtitle={t.hero.subtitle}
        ctaText={t.hero.cta}
        ctaOnClick={scrollToCalc}
      />

      <div className="calc" ref={calcRef}>
        <div className="calc__card">
          <div className="calc__grid">
            <div className="calc__field">
              <label className="calc__label">{t.form.reviewCount}</label>
              <input
                className="calc__input"
                type="number"
                min="1"
                placeholder={t.form.reviewCountPlaceholder}
                value={reviewCount}
                onChange={(e) => setReviewCount(e.target.value)}
              />
            </div>

            <div className="calc__field">
              <label className="calc__label">{t.form.currentRating}</label>
              <input
                className="calc__input"
                type="number"
                min="1"
                max="5"
                step="0.1"
                placeholder={t.form.currentRatingPlaceholder}
                value={currentRating}
                onChange={(e) => setCurrentRating(e.target.value)}
              />
            </div>

            <div className="calc__field calc__field--full">
              <label className="calc__label">{t.form.targetRating}</label>
              <div className="calc__target-row">
                <input
                  className="calc__input"
                  type="number"
                  min="1"
                  max="5"
                  step="0.1"
                  placeholder={t.form.targetRatingPlaceholder}
                  value={targetRating}
                  onChange={(e) => setTargetRating(e.target.value)}
                />
                <div className="calc__presets">
                  {PRESETS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`calc__preset${parseFloat(targetRating) === p ? " calc__preset--active" : ""}`}
                      onClick={() => setTargetRating(String(p))}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {result && (
            <div className="calc__result">
              {result.alreadyMet ? (
                <div className="calc__result-card calc__result-card--success">
                  <span className="calc__result-icon">✅</span>
                  <div>
                    <p className="calc__result-title">{t.result.alreadyMetTitle}</p>
                    <p className="calc__result-desc">{t.result.alreadyMetDesc}</p>
                  </div>
                </div>
              ) : result.unreachable ? (
                <div className="calc__result-card calc__result-card--warning">
                  <span className="calc__result-icon">⚠️</span>
                  <div>
                    <p className="calc__result-title">{t.result.unreachableTitle}</p>
                    <p className="calc__result-desc">{t.result.unreachableDesc}</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="calc__result-card">
                    <div className="calc__result-number">{result.needed}</div>
                    <div>
                      <p className="calc__result-title">{t.result.neededTitle}</p>
                      <p className="calc__result-desc">
                        {t.result.neededDesc(currentRating, result.newRating, result.newTotal)}
                      </p>
                    </div>
                  </div>

                  <div className="calc__breakdown">
                    <div className="calc__stat">
                      <span className="calc__stat-value">⭐ {currentRating}</span>
                      <span className="calc__stat-label">{t.result.currentRating}</span>
                    </div>
                    <div className="calc__stat-arrow">→</div>
                    <div className="calc__stat">
                      <span className="calc__stat-value">⭐ {result.newRating}</span>
                      <span className="calc__stat-label">{t.result.projectedRating}</span>
                    </div>
                    <div className="calc__stat-arrow">|</div>
                    <div className="calc__stat">
                      <span className="calc__stat-value">{reviewCount}</span>
                      <span className="calc__stat-label">{t.result.currentReviews}</span>
                    </div>
                    <div className="calc__stat-arrow">+</div>
                    <div className="calc__stat">
                      <span className="calc__stat-value">{result.needed}</span>
                      <span className="calc__stat-label">{t.result.reviewsToGet}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <section className="calc-seo">
        <div className="calc-seo__inner">

          <article className="calc-seo__block">
            <h2>{t.why.title}</h2>
            <p>{t.why.text}</p>
            <div className="calc-seo__stats-row">
              {t.why.stats.map((stat) => (
                <div key={stat.number} className="calc-seo__stat-card">
                  <span className="calc-seo__stat-number">{stat.number}</span>
                  <span className="calc-seo__stat-text">{stat.text}</span>
                </div>
              ))}
            </div>
          </article>

          <article className="calc-seo__block">
            <h2>{t.how.title}</h2>
            <p>{t.how.intro}</p>
            <div className="calc-seo__steps">
              {t.how.steps.map((step, i) => (
                <div key={step.title} className="calc-seo__step">
                  <span className="calc-seo__step-num">{i + 1}</span>
                  <div>
                    <strong>{step.title}</strong>
                    <p>{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <p>{t.how.outro}</p>
          </article>

          <article className="calc-seo__block">
            <h2>{t.examples.title}</h2>
            <p>{t.examples.intro}</p>
            <div className="calc-seo__example-row">
              {t.examples.items.map((example) => (
                <div key={example.header} className="calc-seo__example">
                  <div className="calc-seo__example-header">{example.header}</div>
                  <div className="calc-seo__example-body">
                    <span className="calc-seo__example-big">{example.big}</span>
                    <span>{t.examples.itemText}</span>
                  </div>
                </div>
              ))}
            </div>
            <p>{t.examples.outro}</p>
          </article>

          <article className="calc-seo__block">
            <h2>{t.ideal.title}</h2>
            <div className="calc-seo__scale">
              <div className="calc-seo__scale-zone calc-seo__scale-zone--bad">
                <span className="calc-seo__scale-range">&lt; 4.0</span>
                <span className="calc-seo__scale-label">{t.ideal.scale.bad}</span>
              </div>
              <div className="calc-seo__scale-zone calc-seo__scale-zone--ok">
                <span className="calc-seo__scale-range">4.0 — 4.2</span>
                <span className="calc-seo__scale-label">{t.ideal.scale.ok}</span>
              </div>
              <div className="calc-seo__scale-zone calc-seo__scale-zone--good">
                <span className="calc-seo__scale-range">4.3 — 4.7</span>
                <span className="calc-seo__scale-label">{t.ideal.scale.good}</span>
              </div>
              <div className="calc-seo__scale-zone calc-seo__scale-zone--sus">
                <span className="calc-seo__scale-range">4.8 — 5.0</span>
                <span className="calc-seo__scale-label">{t.ideal.scale.sus}</span>
              </div>
            </div>
            <p>{t.ideal.text}</p>
          </article>

          <article className="calc-seo__block">
            <h2>{t.tips.title}</h2>
            <p>{t.tips.intro}</p>
            <div className="calc-seo__tips">
              {t.tips.items.map((tip) => (
                <div key={tip.title} className="calc-seo__tip">
                  <span className="calc-seo__tip-icon">{tip.icon}</span>
                  <div>
                    <strong>{tip.title}</strong>
                    <p>{tip.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </article>

          <article className="calc-seo__block">
            <h2>{t.seoImpact.title}</h2>
            <p>{t.seoImpact.intro}</p>
            <div className="calc-seo__factors">
              {t.seoImpact.factors.map((factor) => (
                <div key={factor.title} className="calc-seo__factor">
                  <span className="calc-seo__factor-icon">{factor.icon}</span>
                  <strong>{factor.title}</strong>
                  <p>{factor.text}</p>
                </div>
              ))}
            </div>
            <p>{t.seoImpact.outro}</p>
          </article>

        </div>
      </section>

      <PageFooter />
    </div>
  );
};

export default ReviewCalculator;
